import { smartSort } from '@ardoq/pagination';

import {
  DatasourceTable,
  newTableTheme,
  useSortableTable,
} from '@ardoq/table-legacy';

import { ReactNode, CSSProperties, RefObject, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { ColumnHeader } from './ColumnHeader';
import { s16, s32, colors } from '@ardoq/design-tokens';
import { ComplaintLevel, DataValue, TableColumn } from './types';
import { WithPopover, TruncatedTextPopover } from '@ardoq/popovers';
import { type I18nLocale } from '@ardoq/i18n';

type TableParams<T extends Record<string, DataValue>> = {
  i18nLocale: I18nLocale;
  dataSource: T[];
  style?: CSSProperties;
  getComplaint: (
    columnIndex: number | null,
    rowIndex: number | null
  ) => { level: ComplaintLevel; message: string } | null;
  getRowOriginalIndex: (row: T) => number;
  columns: TableColumn[];
  setSelectedColumn: (column: number) => void;
  selectedColumnIndex: number | null;
  loading?: boolean;
  idIndex?: string;
  advancedLabel?: string;
  confirmLabel?: string;
  clickToConfigureLabel?: string;
};

export const Table = <T extends Record<string, DataValue>>({
  i18nLocale,
  dataSource,
  columns,
  style,
  setSelectedColumn,
  getComplaint,
  selectedColumnIndex,
  getRowOriginalIndex,
  loading = false,
  idIndex = '_id',
  advancedLabel,
  confirmLabel,
  clickToConfigureLabel,
}: TableParams<T>): ReactNode => {
  const { sortOrder, sortBy } = useSortableTable({
    initialSortBy: 'name',
    skipDefaultSortOrder: true,
  });
  const sortedDataSource = smartSort(dataSource, sortBy, sortOrder);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (selectedColumnIndex !== null) {
      adjustTableScrollPosition({
        selectedColumnIndex,
        componentRef: tableContainerRef,
      });
    }
  }, [selectedColumnIndex]);

  return (
    <div ref={tableContainerRef}>
      <TableContainer
        idIndex={idIndex}
        loading={loading}
        style={style}
        components={newTableTheme}
        dataSource={sortedDataSource}
        columns={columns.map((column, index) => ({
          dataIndex: column.dataIndex,
          valueRender: (value: string, datasourceRow: unknown) => {
            return renderCell({
              value,
              column,
              complaint: getComplaint(
                index === 0 ? null : index,
                getRowOriginalIndex(datasourceRow as T)
              ),
            });
          },
          headerRender: () => (
            <ColumnHeader
              context={{ i18nLocale }}
              title={column.title}
              config={column.config}
              lastColumn={index === columns.length - 1}
              onConfirm={column.onConfirm}
              isReadyForMapping={column.isConfigurableTable}
              hasActiveFilters={column.hasActiveFilters}
              onFilter={column.onFilter}
              onSelectColumn={() => setSelectedColumn(index)}
              isHighlighted={column.isHighlighted}
              complaint={getComplaint(index, null)}
              hideConfig={column.hideConfig}
              advancedLabel={advancedLabel}
              confirmLabel={confirmLabel}
              clickToConfigureLabel={clickToConfigureLabel}
            />
          ),
        }))}
      />
    </div>
  );
};

const renderCell = ({
  value,
  column,
  complaint,
}: {
  value: string;
  column: TableColumn;
  complaint: { level: ComplaintLevel; message: string } | null;
}): ReactNode => {
  if (column.isHighlighted) {
    return (
      <HighLightedCell>
        <TruncatedTextPopover>{value}</TruncatedTextPopover>
      </HighLightedCell>
    );
  }
  if (!complaint) {
    return (
      <Cell>
        <TruncatedTextPopover>{value}</TruncatedTextPopover>
      </Cell>
    );
  }
  if (complaint.level === 'error') {
    return (
      <WithPopover content={complaint.message}>
        <ErrorCell>{value}</ErrorCell>
      </WithPopover>
    );
  }
  if (complaint.level === 'warn') {
    return (
      <WithPopover content={complaint.message}>
        <WarningCell>{value}</WarningCell>
      </WithPopover>
    );
  }
  return <Cell>{value}</Cell>;
};

const adjustTableScrollPosition = ({
  componentRef,
  selectedColumnIndex,
}: {
  componentRef: RefObject<HTMLDivElement | null>;
  selectedColumnIndex: number;
}): void => {
  const table = componentRef.current?.getElementsByTagName('table')[0];
  const tableContainer = table?.parentElement;
  const selectedHead = tableContainer
    ? table.getElementsByTagName('th')[selectedColumnIndex]
    : null;
  if (
    table instanceof HTMLElement &&
    tableContainer instanceof HTMLElement &&
    selectedHead instanceof HTMLElement &&
    tableContainer.scrollTo instanceof Function
  ) {
    const tableBounds = table.getBoundingClientRect();

    const tableContainerBounds = tableContainer.getBoundingClientRect();

    const leftBound = tableContainerBounds.left;
    const rightBound = tableContainerBounds.right;

    const selectedHeadBounds = selectedHead.getBoundingClientRect();

    const currentScrollOffset = leftBound - tableBounds.left;

    if (selectedColumnIndex === 1) {
      tableContainer.scrollTo({ left: 0, behavior: 'smooth' });
    } else if (selectedHeadBounds.right > rightBound) {
      tableContainer.scrollTo({
        left: Math.min(
          // this min expression is intended to handle the case where the scrolling to the right is going to far
          // and therefore hiding the left part of the selected column, we want to prioritize the visibility of the left side.
          currentScrollOffset - (leftBound - selectedHeadBounds.left),
          currentScrollOffset + selectedHeadBounds.right - rightBound + 50 // the magic number has shrink by half, but is still there, maybe due to the padding of the table container
        ),
        behavior: 'smooth',
      });
    } else if (selectedHeadBounds.left < leftBound) {
      tableContainer.scrollTo({
        left: currentScrollOffset - (leftBound - selectedHeadBounds.left),
        behavior: 'smooth',
      });
    }
  }
};

/** 10px padding due to custom implementation */
const Cell = styled.div`
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 10px ${s16};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HighLightedCell = styled(Cell)`
  background-color: ${colors.blue50};
`;

const WarningCell = styled(Cell)`
  background-color: ${colors.yellow50};
  border: 1px solid ${colors.yellow500};
`;

const ErrorCell = styled(Cell)`
  background-color: ${colors.red50};
  border: 1px solid ${colors.red200};
`;

const TableContainer = styled(DatasourceTable)`
  padding-left: ${s32};
  padding-bottom: ${s16};
  min-height: 300px;
  table {
    width: auto;
    height: auto;
    max-width: 100%;
  }

  thead {
    height: 100%;
    &::after {
      top: 0;
    }
    /* Firefox-specific styles */
    @-moz-document url-prefix() {
      & > tr > th {
        height: 100%;
      }
    }
  }
  th {
    padding-bottom: 0px;
    & > div {
      height: 100%;
      &:first-child {
        padding: 0;
      }
    }
  }
  td,
  th {
    padding: 0;
    max-width: 450px;
    white-space: pre;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
